import React, { useEffect, useState } from "react";
import { Box, Button, HStack } from "@chakra-ui/react";
import { Modal } from "components";
import { useDrawing, useCurrentCase } from "duck";
import Captions from "../../../../../captions";
import Comments from "../../../../../comments";
import Jaw from "../../../../../jaw";
import JawWrapper from "../../../../../jaw-wrapper";
import { CustomCursor, DrawingTool, Tools } from "./components";
import { useDeleteDentalSketchComments, useUpdateDentalSketch } from "./duck";

interface QuickDrawModalProps {
  onClose: () => void;
}

const QuickDrawModal: React.FC<QuickDrawModalProps> = ({ onClose }) => {
  const dentalSketch = useCurrentCase(
    ({ data }) => data.client.patient.dentalSketch
  );

  const {
    canvasRef,
    startDrawing,
    draw,
    stopDrawing,
    toolsConfig
  } = useDrawing("100%", "100%", dentalSketch);

  const { update, loading } = useUpdateDentalSketch();

  const [clearLoading, setClearLoading] = useState(false);
  const deleteComments = useDeleteDentalSketchComments();

  const handleClose = () => {
    Modal.instance.open({
      title: "Close QuickDraw",
      description:
        "Are you sure you want to close? Changes you made won't be saved.",
      okText: "Close",
      onOk() {
        onClose();
      }
    });
  };

  const handleSubmit = async () => {
    if (canvasRef.current) {
      const base64 = canvasRef.current.toDataURL();
      await update({
        base64Format: base64,
        name: "dental-sketch"
      });
      onClose();
    }
  };

  const keyDownHandler = (e: KeyboardEvent) => {
    const isMac = navigator.platform.toUpperCase().indexOf("MAC") >= 0;
    const isCmdOrCtrlPressed = (isMac && e.metaKey) || (!isMac && e.ctrlKey);
    const isZKeyPressed = e.key === "z";
    const isYKeyPressed = e.key === "y";
    if (isCmdOrCtrlPressed && isZKeyPressed) {
      e.preventDefault();
      toolsConfig.undo();
    }

    if (isCmdOrCtrlPressed && isYKeyPressed) {
      e.preventDefault();
      toolsConfig.redo();
    }
  };

  const handleClear = async () => {
    setClearLoading(true);
    await update(null);
    if (deleteComments) {
      await deleteComments();
    }
    toolsConfig.clear();
    setClearLoading(false);
    onClose();
  };

  useEffect(() => {
    document.addEventListener("keydown", keyDownHandler);

    return () => {
      document.removeEventListener("keydown", keyDownHandler);
    };
  }, []);

  return (
    <Modal
      isCentered={false}
      title="QuickDraw"
      isOpen
      onClose={handleClose}
      size={{ sm: "2xl", xl: "5xl" }}
      closeOnOverlayClick={false}
      blockScrollOnMount={false}
    >
      <Tools
        clearHandler={{ clear: handleClear, isLoading: clearLoading }}
        toolsConfig={toolsConfig}
      />
      <Box>
        <Comments enabled={toolsConfig.mode === "disabled"}>
          <JawWrapper my={6}>
            <DrawingTool
              canvasRef={canvasRef}
              stopDrawing={stopDrawing}
              startDrawing={startDrawing}
              draw={draw}
              toolsConfig={toolsConfig}
            />
            <Jaw />
            <Captions />
            <CustomCursor config={toolsConfig} canvasRef={canvasRef} />
          </JawWrapper>
        </Comments>
      </Box>
      <Tools
        clearHandler={{ clear: handleClear, isLoading: clearLoading }}
        toolsConfig={toolsConfig}
      />

      <HStack spacing={4} justifyContent="flex-end" pt={4}>
        <Button
          color="gray.700"
          variant="outline"
          colorScheme="gray"
          onClick={handleClose}
        >
          Cancel
        </Button>
        <Button onClick={handleSubmit} isLoading={loading} type="submit">
          Save
        </Button>
      </HStack>
    </Modal>
  );
};

export default QuickDrawModal;
